import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import StatusDialog from "./StatusDialog";

const StatusDialogContext = createContext({
  showStatus: () => {},
});

const severityMatchers = [
  { regex: /(error|fail|failed|invalid|missing|denied|unable)/i, severity: "error" },
  { regex: /(success|saved|done|completed|updated|added)/i, severity: "success" },
  { regex: /(warn|caution|careful|attention)/i, severity: "warning" },
];

const defaultTitleBySeverity = {
  success: "Success",
  error: "Error",
  warning: "Warning",
  info: "Notification",
};

function inferSeverity(text = "") {
  if (typeof text !== "string") return "info";
  const match = severityMatchers.find((matcher) => matcher.regex.test(text));
  return match?.severity || "info";
}

function normalizePayload(payload) {
  if (!payload) return null;
  if (typeof payload === "string") {
    const severity = inferSeverity(payload);
    return {
      severity,
      description: payload,
      title: defaultTitleBySeverity[severity],
    };
  }
  if (React.isValidElement(payload)) {
    return {
      severity: "info",
      description: payload,
      title: defaultTitleBySeverity.info,
    };
  }
  if (typeof payload === "object") {
    const severity = payload.severity || "info";
    const description = payload.description ?? payload.message ?? payload.content ?? "";
    return {
      ...payload,
      severity,
      description,
      title: payload.title || defaultTitleBySeverity[severity],
    };
  }
  return {
    severity: "info",
    description: String(payload),
    title: defaultTitleBySeverity.info,
  };
}

export function StatusDialogProvider({ children }) {
  const [queue, setQueue] = useState([]);
  const [current, setCurrent] = useState(null);

  const showStatus = useCallback((rawPayload) => {
    const normalized = normalizePayload(rawPayload);
    if (!normalized) return;
    setQueue((prev) => [...prev, normalized]);
  }, []);

  const contextValue = useMemo(() => ({ showStatus }), [showStatus]);

  useEffect(() => {
    if (!current && queue.length > 0) {
      setCurrent(queue[0]);
      setQueue((prev) => prev.slice(1));
    }
  }, [current, queue]);

  useEffect(() => {
    const originalAlert = window.alert;
    window.alert = (message) => {
      showStatus(message);
    };
    return () => {
      window.alert = originalAlert;
    };
  }, [showStatus]);

  const handleClose = () => {
    setCurrent(null);
  };

  return (
    <StatusDialogContext.Provider value={contextValue}>
      {children}
      <StatusDialog
        open={Boolean(current)}
        severity={current?.severity}
        title={current?.title}
        description={current?.description}
        actions={current?.actions || []}
        dialogProps={current?.dialogProps}
        onClose={() => {
          current?.onClose?.();
          handleClose();
        }}
      />
    </StatusDialogContext.Provider>
  );
}

export function useStatusDialog() {
  return useContext(StatusDialogContext);
}
